// LeadRecommendationsPanel — Phase 3.
//
// Deterministic cross-sell / product recommendations from
// /api/leads/recommendations. Every rec traces to a real signal on
// the lead + Phase 2 insurance profile. No hallucinated products.
//
// Refetches whenever the lead payload changes so a newly-added
// profile field immediately updates the ranking.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';

const PRODUCT_LABEL = {
  aca_ppo:            'ACA PPO',
  aca_hmo:            'ACA HMO',
  private_ppo:        'Private PPO',
  accident:           'Accident plan',
  dental_vision:      'Dental + vision',
  critical_illness:   'Critical illness',
  hospital_indemnity: 'Hospital indemnity',
  life_term:          'Term life',
  life_iul:           'IUL',
  medicare_advantage: 'Medicare Advantage',
  medicare_supp:      'Medicare Supplement',
  medicare_pdp:       'Medicare Part D',
};

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmt$(cents) {
  if (!Number.isFinite(cents) || cents === 0) return '';
  return `$${(cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function priorityStyle(p) {
  if (p === 1) return { bg: '#dcfce7', border: '#86efac', text: '#166534', label: 'HIGH' };
  if (p === 2) return { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', label: 'MED' };
  return { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', label: 'LOW' };
}

export default function LeadRecommendationsPanel({ lead }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  React.useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const { authedFetchLead } = await import('./authedFetch');
      const res = await authedFetchLead('/api/leads/recommendations', { lead }, controller.signal);
      if (controller.signal.aborted) return;
      setState({ loading: false, data: res.ok ? res.data : null, error: res.ok ? null : res.error });
    })();
    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  if (state.loading) return null;
  if (state.error) {
    return (
      <div style={{
        background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10,
        padding: 10, fontSize: 11, color: '#92400e', marginBottom: 8,
      }}>
        AI Recommendations temporarily unavailable ({String(state.error)}).
      </div>
    );
  }
  if (!state.data) return null;

  const recs = state.data.recommendations || [];
  if (recs.length === 0) return null;

  return (
    <div style={{
      background: '#ecfdf5',
      border: '1px solid #a7f3d0',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#047857', fontWeight: 800 }}>
          AI Recommendations
        </div>
        <div style={{ fontSize: 11, color: '#065f46' }}>
          Ranked cross-sells for this lead
        </div>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {recs.map((r, i) => {
          const p = priorityStyle(r.priority);
          const comm = fmt$(r.estimated_commission_cents);
          return (
            <div key={`${r.product}-${i}`} style={{
              display: 'flex', gap: 12,
              background: '#ffffff', border: '1px solid #d1fae5',
              borderRadius: 8, padding: '10px 12px',
            }}>
              <div style={{
                background: p.bg, color: p.text, border: `1px solid ${p.border}`,
                padding: '3px 8px', borderRadius: 4,
                fontSize: 9, letterSpacing: '0.1em', fontWeight: 800, alignSelf: 'flex-start',
                minWidth: 40, textAlign: 'center',
              }}>{p.label}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#064e3b' }}>
                  {PRODUCT_LABEL[r.product] || r.product}
                  {comm && (
                    <span style={{
                      color: '#047857', fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 11, marginLeft: 10, fontWeight: 700,
                    }}>~{comm} commission</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#065f46', marginTop: 3 }}>
                  {r.reason}
                </div>
                <div style={{ fontSize: 11, color: '#0d9488', marginTop: 2, fontStyle: 'italic' }}>
                  Why now: {r.why_now}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
